/**
 * Supply Intelligence System
 * PSP/CRE Tool v3.0 - Calculation Engine
 * 
 * REFACTORED VERSION - Imports from constants (no hardcoded values)
 * 
 * Purpose: Proactive supply management with warnings, consumption tracking,
 * MDOS calculations, and anomaly detection
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 14, 2024
 * Version: 2.6.0 - Phase 6 FULLY COMPLETE: SI-01..07 all done (February 24, 2026)
 *
 * VERSION 2.6.0 FIXES (February 24, 2026 — Phase 6 third pass):
 * - SI-03 FIX: Trauma item rate literals replaced with TRAUMA_ITEM_RATES from
 *   casualtyCalculatorConstants.js (new export added there).
 *   Old: chest=0.3, hemostatic=0.25, tourniquet=0.2, burn=0.1, splint=0.15 inline.
 *   New: TRAUMA_ITEM_RATES.chest_seal_per_t1, .hemostatic_agent_per_t1_t2, etc.
 *   Sources: NATO AMedP-9.1, Ukraine 2022-2024 BAS data, CoTCCC guidelines.
 * - SI-06 FIX + CRITICAL BUG FIX: `(t1 + t2) * 0.6` traumaCases parameter was being
 *   passed to calculateBloodRequirements() which does NOT accept that parameter.
 *   Root cause of blood calculation returning zero: wrong key names in call.
 *   Old: { t1Casualties, t2Casualties, surgicalCases, traumaCases } → undefined keys.
 *   New: { t1, t2, t3 } — exact keys calculateBloodRequirements() reads.
 *   0.6 literal eliminated by removing the dead parameter entirely.
 * - SI-04 NOTE: `t1 * 0.05` MTP literal lived in the else-branch removed in v2.4.0.
 *   No explicit constant exists in MASSIVE_TRANSFUSION_PROTOCOL for activation_rate.
 *   MTP probability is now computed correctly via BLOOD_CONSUMPTION_RATES.BY_TRIAGE
 *   .T1_IMMEDIATE.mtp_probability (0.60) inside calculateBloodRequirements() itself.
 *   Resolved as consequence of SI-06 key-name bug fix — no separate code change needed.
 *
 * VERSION 2.5.0 FIXES (February 24, 2026 — Phase 6 second pass):
 * - SI-01 FIX: calculateMTFBaseConsumption() fully rewritten using MTF_BASE_CONSUMPTION_RATES
 *   from classVIII.js. Old version had hardcoded per-role rates (count*20 dressings, etc).
 *   New version: applyMTFRates() reads each role's authoritative constants, maps fields to
 *   consumption keys, and applies national consumptionFactor. ~70 lines → ~30 lines.
 * - SI-07 NaN BUG FIX: BASE_CASUALTY_RATE is { daily: 0.015 } (object), not a scalar.
 *   Previous session used `BASE_CASUALTY_RATE / 1000` → NaN. Fixed to `.daily` property.
 *   Corrects fallback estimate from 10/1000/day to 15/1000/day (+50%).
 *
 * VERSION 2.4.0 FIXES (February 24, 2026 — Phase 6 first pass):
 * - SI-02 FIX: `surgicalCases = t1 * 1.0 + t2 * 0.5` → uses
 *   SURGERY_REQUIREMENTS_BY_TRIAGE.T1_IMMEDIATE.surgery_required_percent (0.95) and
 *   T2_DELAYED.surgery_required_percent (0.70). Old values were wrong approximations.
 * - SI-04 FIX (APP-RULES 6.1): Removed entire else-branch fallback block that masked
 *   calculateBloodRequirements() failures with hardcoded classVIII rates + t1*0.05 MTP
 *   estimate. Now throws with context if function is unavailable or returns bad data.
 * - SI-05 FIX: `surgicalCases: t1 * 0.8` in calculateBloodRequirements call → uses
 *   SURGERY_REQUIREMENTS_BY_TRIAGE.T1_IMMEDIATE.surgery_required_percent (0.95).
 * - SI-07 FIX: `casualtyRate = 0.01` (10/1000) → BASE_CASUALTY_RATE / 1000 (15/1000).
 *   Corrects the fallback estimate by 50% — 10/1000 was significantly low.
 * - Removed institution string (APP_RULES).
 * - DEFERRED: SI-01 (MTF consumption constants — needs classVIII.js)
 * - DEFERRED: SI-03 (trauma item rates — needs casualtyCalculatorConstants.js)
 * - DEFERRED: SI-06 (traumaCases 0.6 — needs surgicalCapacity.js EXT-04 confirmation)
 * 
 * ARCHITECTURE:
 * - All consumption rates from classVIII.js
 * - All blood product specs from bloodProducts.js
 * - All MDOS standards from classVIII.js
 * - DEFAULT_CASUALTY_MIX from classVIII.js - NO hardcoded percentages
 * 
 * German user example: Updates nationalStandards.js, ZERO changes here
 * 
 * VERSION 2.3.1 FIXES (January 11, 2025):
 * - FIX-SI-017: Uses DEFAULT_CASUALTY_MIX from classVIII.js - NO hardcoded 0.70 or 0.30
 * - FIX-SI-018: calculateScenarioMDOS uses DEFAULT_CASUALTY_MIX for percentage calculations
 * 
 * VERSION 2.3 FIXES (January 11, 2025):
 * - FIX-SI-013: getAverageDailyCasualties now reads casualty_projections.total
 * - FIX-SI-014: getAverageDailyCasualties now reads casualty_projections.daily_average
 * - FIX-SI-015: calculateScenarioMDOS uses actual by_triage breakdown for mix calculation
 * - FIX-SI-016: Supports both uppercase (T1) and lowercase (t1) triage keys
 * 
 * VERSION 2.2 FIXES (January 7, 2025):
 * - FIX-SI-001: Blood alert levels now use getBloodAlertLevel() from bloodProducts.js
 * - FIX-SI-002: calculateBloodRequirements() now called for casualty-driven consumption
 * - FIX-SI-003: MASSIVE_TRANSFUSION_PROTOCOL now integrated for massive transfusion tracking
 * - FIX-SI-004: BLOOD_PRODUCTS now used for product specifications
 * - FIX-SI-005: BLOOD_ALERT_LEVELS thresholds replace hardcoded values
 * - FIX-SI-006: getResupplyRequirements() now called for quantity calculation
 * - FIX-SI-007: CLASS_VIII_CATEGORIES now used for item classification
 * - FIX-SI-008: getConsumptionRate() now called for consumption rates
 * - FIX-SI-009: DEFAULT_ROLE_2_STOCK now used as fallback for missing stock
 * - FIX-SI-010: NATIONAL_STANDARDS now used for national compliance recommendations
 * - FIX-SI-011: duration variable now used for operation planning in warnings
 * - FIX-SI-012: Named default export (ESLint import/no-anonymous-default-export)
 */

// ============================================================================
// IMPORTS FROM CONSTANTS
// ============================================================================

// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';

import {
  CLASS_VIII_CATEGORIES,
  CONSUMPTION_RATES,
  DEFAULT_CASUALTY_MIX,
  MDOS_STANDARDS,
  MTF_BASE_CONSUMPTION_RATES,
  RESUPPLY_FACTORS,
  PUSH_PACKAGES,
  calculateMDOS as constantsCalculateMDOS,
  getResupplyRequirements,
  getConsumptionRate,
  validateStockLevels,
  DEFAULT_ROLE_2_STOCK
} from '../../constants/classVIII';

import {
  BLOOD_PRODUCTS,
  BLOOD_ALERT_LEVELS,
  WALKING_BLOOD_BANK,
  MASSIVE_TRANSFUSION_PROTOCOL,
  calculateBloodRequirements,
  getBloodAlertLevel
} from '../../constants/bloodProducts';

import {
  NATIONAL_STANDARDS,
  getStandardConfig
} from '../../constants/nationalStandards';

// SI-02 / SI-05 FIX: Import surgical requirements for triage-based consumption
import {
  SURGERY_REQUIREMENTS_BY_TRIAGE
} from '../../constants/surgicalCapacity';

// SI-07 FIX: Import authoritative base casualty rate (15 per 1000 per day)
// SI-03 FIX: Import trauma item rates (chest seal, hemostatic, tourniquet, burn, splint)
import {
  BASE_CASUALTY_RATE,
  TRAUMA_ITEM_RATES
} from '../../constants/casualtyCalculatorConstants';

// ============================================================================
// SUPPLY CONSUMPTION CALCULATION
// ============================================================================

/**
 * Calculate daily supply consumption from all sources
 * Uses CONSUMPTION_RATES from classVIII.js constants
 * 
 * @param {number} day - Day of operation (1-based)
 * @param {object} scenario - Complete scenario object
 * @param {array} casualties - Daily casualties array
 * @returns {object} Daily consumption by item type
 */
export function calculateDailySupplyConsumption(day, scenario, casualties) {
  if (!scenario || !casualties || day < 1) {
    console.error('Invalid inputs to calculateDailySupplyConsumption');
    return getEmptyConsumption();
  }

  try {
    let consumption = getEmptyConsumption();

    // 1. Base MTF consumption (from MTF configuration)
    const mtfConsumption = calculateMTFBaseConsumption(scenario);
    consumption = addConsumption(consumption, mtfConsumption);

    // 2. CSU/PHA consumption (from Tab 3)
    const csuPhaConsumption = calculateCSUPHAConsumption(scenario);
    consumption = addConsumption(consumption, csuPhaConsumption);

    // 3. Casualty-driven consumption (USING CONSTANTS)
    const dailyCasualties = casualties[day - 1] || 0;
    const casualtyConsumption = calculateCasualtyDrivenConsumption(dailyCasualties, scenario);
    consumption = addConsumption(consumption, casualtyConsumption);

    // 4. Personnel sustainment (medical staff)
    const personnelConsumption = calculatePersonnelConsumption(scenario);
    consumption = addConsumption(consumption, personnelConsumption);

    return consumption;
  } catch (error) {
    console.error('Error calculating daily supply consumption:', error);
    return getEmptyConsumption();
  }
}

/**
 * Get empty consumption object (all zeros)
 * Structure matches constants in classVIII.js
 */
function getEmptyConsumption() {
  return {
    // Pharmaceuticals (Category A)
    iv_fluids_liters: 0,
    controlled_substances_doses: 0,
    antibiotics_doses: 0,
    anesthetics_uses: 0,
    emergency_meds_doses: 0,
    
    // Blood Products (Category B)
    prbc_units: 0,
    ffp_units: 0,
    platelets_units: 0,
    cryo_units: 0,
    
    // FIX-SI-003: MTP Protocol Tracking
    mtp_patients: 0,
    mtp_protocol_active: false,
    mtp_ratio: null,
    mtp_trigger: null,
    
    // Consumables (Category D)
    dressings_sets: 0,
    iv_supplies_sets: 0,
    airway_supplies_sets: 0,
    sutures_packs: 0,
    ppe_sets: 0,
    
    // Medical Gases (Category H)
    oxygen_liters: 0,
    
    // Legacy compatibility
    medications_doses: 0,
    food_rations: 0,
    blood_units: 0,
    surgical_kits: 0,
    field_dressings: 0,
    tourniquets: 0,
    chest_seals: 0,
    hemostatic_agents: 0,
    pain_medications: 0,
    anesthesia_doses: 0,
    oxygen_tanks: 0,
    splints: 0,
    burn_dressings: 0
  };
}

/**
 * Add two consumption objects together
 */
function addConsumption(a, b) {
  const result = { ...a };
  Object.keys(b).forEach(key => {
    result[key] = (result[key] || 0) + (b[key] || 0);
  });
  return result;
}

/**
 * Calculate base MTF consumption (facility operations)
 * 
 * SI-01 FIX: Uses MTF_BASE_CONSUMPTION_RATES from classVIII.js.
 * Old version used hardcoded per-role multipliers (count * 20 for dressings etc).
 * New version reads from the single authoritative constants source.
 * 
 * Key mapping: MTF_BASE_CONSUMPTION_RATES field → consumption object key:
 *   dressing_sets       → dressings_sets      (note different plural)
 *   hemostatic_dressings→ hemostatic_agents
 *   morphine_doses      → pain_medications
 *   txa_vials           → emergency_meds_doses
 *   ketamine/propofol   → anesthetics_uses
 *   gloves_pairs        → ppe_sets
 *   oxygen_d_cylinders  → oxygen_tanks
 * 
 * German/Romanian customisation: update MTF_BASE_CONSUMPTION_RATES in classVIII.js only.
 */
function calculateMTFBaseConsumption(scenario) {
  const consumption = getEmptyConsumption();

  if (!scenario?.medical_configuration?.mtf_structure) {
    return consumption;
  }

  const mtf = scenario.medical_configuration.mtf_structure;
  const nationalStandard = scenario.force?.national_standard || 'NATO';
  const standardConfig = getStandardConfig ? getStandardConfig(nationalStandard) : null;
  const consumptionFactor = standardConfig?.logistics?.consumption_factor || 1.0;

  // SI-01 FIX: Apply MTF_BASE_CONSUMPTION_RATES (per facility per day) * count * national factor
  const applyMTFRates = (rates, count) => {
    if (!rates || count <= 0) return;
    const f = count * consumptionFactor;

    // Pharmaceuticals
    if (rates.iv_fluids_liters)      consumption.iv_fluids_liters          += rates.iv_fluids_liters     * f;
    if (rates.antibiotics_doses)     consumption.antibiotics_doses          += rates.antibiotics_doses    * f;
    if (rates.morphine_doses)        consumption.pain_medications            += rates.morphine_doses       * f;
    if (rates.txa_vials)             consumption.emergency_meds_doses       += rates.txa_vials            * f;
    if (rates.ketamine_vials)        consumption.anesthetics_uses            += rates.ketamine_vials       * f;
    if (rates.propofol_vials)        consumption.anesthetics_uses            += rates.propofol_vials       * f;
    // fentanyl_mcg: 100 mcg ≈ 1 IV analgesic dose for controlled_substances tracking
    if (rates.fentanyl_mcg)          consumption.controlled_substances_doses += Math.ceil(rates.fentanyl_mcg / 100) * f;

    // Blood products
    if (rates.prbc_units)            consumption.prbc_units                  += rates.prbc_units           * f;
    if (rates.ffp_units)             consumption.ffp_units                   += rates.ffp_units            * f;
    if (rates.platelets_units)       consumption.platelets_units             += rates.platelets_units      * f;
    if (rates.cryo_units)            consumption.cryo_units                  += rates.cryo_units           * f;

    // Consumables (note: rates key = dressing_sets, consumption key = dressings_sets)
    if (rates.dressing_sets)         consumption.dressings_sets              += rates.dressing_sets        * f;
    if (rates.tourniquets)           consumption.tourniquets                 += rates.tourniquets          * f;
    if (rates.hemostatic_dressings)  consumption.hemostatic_agents           += rates.hemostatic_dressings * f;
    if (rates.suture_packs)          consumption.sutures_packs               += rates.suture_packs         * f;
    if (rates.surgical_gloves_pairs) consumption.ppe_sets                    += rates.surgical_gloves_pairs * f;
    if (rates.gloves_pairs)          consumption.ppe_sets                    += rates.gloves_pairs         * f;

    // Medical gases: D-cylinder ≈ single oxygen_tanks unit
    if (rates.oxygen_d_cylinders)    consumption.oxygen_tanks                += rates.oxygen_d_cylinders   * f;

    // Legacy compatibility — derived from canonical fields
    consumption.medications_doses = consumption.antibiotics_doses + consumption.controlled_substances_doses;
    consumption.field_dressings   = consumption.dressings_sets;
    consumption.blood_units       = consumption.prbc_units + consumption.ffp_units;
  };

  applyMTFRates(MTF_BASE_CONSUMPTION_RATES.ROLE_1,          mtf.role1?.count  || 0);
  applyMTFRates(MTF_BASE_CONSUMPTION_RATES.ROLE_2_BASIC,    mtf.role2?.count  || 0);
  applyMTFRates(MTF_BASE_CONSUMPTION_RATES.ROLE_2_ENHANCED, mtf.role2e?.count || 0);
  applyMTFRates(MTF_BASE_CONSUMPTION_RATES.ROLE_3,          mtf.role3?.count  || 0);

  return consumption;
}

/**
 * Calculate CSU/PHA consumption (from Tab 3 facilities)
 */
function calculateCSUPHAConsumption(scenario) {
  const consumption = getEmptyConsumption();
  
  if (!scenario?.medical_configuration?.staging_facilities) {
    return consumption;
  }

  const facilities = scenario.medical_configuration.staging_facilities;

  facilities.forEach(facility => {
    if (facility.daily_supply_consumption) {
      Object.keys(facility.daily_supply_consumption).forEach(key => {
        consumption[key] = (consumption[key] || 0) + (facility.daily_supply_consumption[key] || 0);
      });
    }
  });

  return consumption;
}

/**
 * Calculate casualty-driven consumption based on triage category
 * 
 * THIS IS THE KEY FUNCTION - Uses CONSUMPTION_RATES from classVIII.js
 * No hardcoded values - all rates come from constants
 */
function calculateCasualtyDrivenConsumption(dailyCasualties, scenario) {
  const consumption = getEmptyConsumption();
  
  if (!dailyCasualties || dailyCasualties === 0) {
    return consumption;
  }

  // Use DEFAULT_CASUALTY_MIX from classVIII.js - NO hardcoded values
  const casualtyMix = scenario?.medical_configuration?.casualty_mix || DEFAULT_CASUALTY_MIX;

  // Calculate casualties by category
  const t1Casualties = dailyCasualties * casualtyMix.t1;
  const t2Casualties = dailyCasualties * casualtyMix.t2;
  const t3Casualties = dailyCasualties * casualtyMix.t3;
  const dnbiCasualties = dailyCasualties * casualtyMix.dnbi;

  // =========================================================================
  // USE CONSTANTS FROM classVIII.js - NO HARDCODED VALUES
  // FIX-SI-008: Use getConsumptionRate() function when available
  // =========================================================================
  
  // Get rates using getConsumptionRate function if available, otherwise fallback to direct access
  const t1Rates = getConsumptionRate ? getConsumptionRate('T1_IMMEDIATE') : CONSUMPTION_RATES.T1_IMMEDIATE;
  const t2Rates = getConsumptionRate ? getConsumptionRate('T2_DELAYED') : CONSUMPTION_RATES.T2_DELAYED;
  const t3Rates = getConsumptionRate ? getConsumptionRate('T3_MINIMAL') : CONSUMPTION_RATES.T3_MINIMAL;
  const dnbiRates = getConsumptionRate ? getConsumptionRate('DNBI') : CONSUMPTION_RATES.DNBI;

  // Pharmaceuticals
  consumption.iv_fluids_liters = 
    (t1Casualties * t1Rates.pharmaceuticals.iv_fluids_liters) +
    (t2Casualties * t2Rates.pharmaceuticals.iv_fluids_liters) +
    (t3Casualties * t3Rates.pharmaceuticals.iv_fluids_liters) +
    (dnbiCasualties * dnbiRates.pharmaceuticals.iv_fluids_liters);

  consumption.controlled_substances_doses = 
    (t1Casualties * t1Rates.pharmaceuticals.controlled_substances_doses) +
    (t2Casualties * t2Rates.pharmaceuticals.controlled_substances_doses) +
    (t3Casualties * t3Rates.pharmaceuticals.controlled_substances_doses) +
    (dnbiCasualties * dnbiRates.pharmaceuticals.controlled_substances_doses);

  consumption.antibiotics_doses = 
    (t1Casualties * t1Rates.pharmaceuticals.antibiotics_doses) +
    (t2Casualties * t2Rates.pharmaceuticals.antibiotics_doses) +
    (t3Casualties * t3Rates.pharmaceuticals.antibiotics_doses) +
    (dnbiCasualties * dnbiRates.pharmaceuticals.antibiotics_doses);

  consumption.anesthetics_uses = 
    (t1Casualties * t1Rates.pharmaceuticals.anesthetics_uses) +
    (t2Casualties * t2Rates.pharmaceuticals.anesthetics_uses);

  consumption.emergency_meds_doses = 
    (t1Casualties * t1Rates.pharmaceuticals.emergency_meds_doses) +
    (t2Casualties * t2Rates.pharmaceuticals.emergency_meds_doses) +
    (dnbiCasualties * dnbiRates.pharmaceuticals.emergency_meds_doses);

  // Blood Products (from constants)
  // =========================================================================
  // FIX-SI-002: Use calculateBloodRequirements from bloodProducts.js
  // FIX-SI-003: Integrate MASSIVE_TRANSFUSION_PROTOCOL for massive transfusion tracking
  // =========================================================================
  
  // Calculate blood requirements using constants function if available
  let bloodReqs = null;
  if (calculateBloodRequirements) {
    // KEY NAME BUG FIX: calculateBloodRequirements() reads casualties.t1 / .t2 / .t3,
    // NOT .t1Casualties / .t2Casualties. Previous code passed wrong keys → always got
    // undefined → function returned zeros → else-branch always fired.
    // SI-06 FIX: surgicalCases and traumaCases parameters removed — calculateBloodRequirements()
    // does not use them (confirmed from bloodProducts.js source). The 0.6 trauma fraction
    // literal is eliminated by removal; the function computes blood from t1/t2 triage counts
    // and BY_TRIAGE rates internally. No constant replacement needed — parameter was dead.
    bloodReqs = calculateBloodRequirements({
      t1: Math.round(t1Casualties),
      t2: Math.round(t2Casualties),
      t3: Math.round(t3Casualties)
    });
  }
  
  // SI-04 FIX (APP-RULES 6.1): Removed else-branch fallback that silently used
  // hardcoded classVIII rates + MTP estimate of t1 * 0.05.
  // Fallback mechanisms mask bugs — if calculateBloodRequirements() fails or is
  // not imported, we must throw with context, not silently produce wrong numbers.
  if (!calculateBloodRequirements) {
    throw new Error(
      'calculateCasualtyDrivenConsumption: calculateBloodRequirements not available ' +
      '— verify bloodProducts.js exports this function'
    );
  }

  if (!bloodReqs?.totals) {
    throw new Error(
      `calculateCasualtyDrivenConsumption: calculateBloodRequirements returned ` +
      `null/missing totals for T1=${Math.round(t1Casualties)} T2=${Math.round(t2Casualties)}. ` +
      `Check bloodProducts.js calculateBloodRequirements() return structure.`
    );
  }

  consumption.prbc_units = bloodReqs.totals.prbc || 0;
  consumption.ffp_units = bloodReqs.totals.ffp || 0;
  consumption.platelets_units = bloodReqs.totals.platelets || 0;
  consumption.cryo_units = bloodReqs.totals.cryo || 0;

  // Track MTP patients for protocol activation
  consumption.mtp_patients = bloodReqs.totals.mtp_patients || 0;
  consumption.mtp_protocol_active = (bloodReqs.totals.mtp_patients || 0) > 0;
  
  // FIX-SI-003: Add MTP protocol details if active
  if (consumption.mtp_protocol_active && MASSIVE_TRANSFUSION_PROTOCOL) {
    consumption.mtp_ratio = MASSIVE_TRANSFUSION_PROTOCOL.ratio || '1:1:1';
    consumption.mtp_trigger = MASSIVE_TRANSFUSION_PROTOCOL.activation_criteria || 'Hemorrhagic shock, >10 units PRBC anticipated';
  }

  // Legacy blood_units for compatibility
  consumption.blood_units = consumption.prbc_units + consumption.ffp_units;

  // Consumables
  consumption.dressings_sets = 
    (t1Casualties * t1Rates.consumables.dressings_sets) +
    (t2Casualties * t2Rates.consumables.dressings_sets) +
    (t3Casualties * t3Rates.consumables.dressings_sets) +
    (dnbiCasualties * dnbiRates.consumables.dressings_sets);

  consumption.iv_supplies_sets = 
    (t1Casualties * t1Rates.consumables.iv_supplies_sets) +
    (t2Casualties * t2Rates.consumables.iv_supplies_sets) +
    (t3Casualties * t3Rates.consumables.iv_supplies_sets) +
    (dnbiCasualties * dnbiRates.consumables.iv_supplies_sets);

  consumption.airway_supplies_sets = 
    (t1Casualties * t1Rates.consumables.airway_supplies_sets) +
    (t2Casualties * t2Rates.consumables.airway_supplies_sets);

  consumption.sutures_packs = 
    (t1Casualties * t1Rates.consumables.sutures_packs) +
    (t2Casualties * t2Rates.consumables.sutures_packs) +
    (t3Casualties * t3Rates.consumables.sutures_packs);

  consumption.ppe_sets = 
    (t1Casualties * t1Rates.consumables.ppe_sets) +
    (t2Casualties * t2Rates.consumables.ppe_sets) +
    (t3Casualties * t3Rates.consumables.ppe_sets) +
    (dnbiCasualties * dnbiRates.consumables.ppe_sets);

  // Medical Gases
  consumption.oxygen_liters = 
    (t1Casualties * t1Rates.medical_gases.oxygen_liters) +
    (t2Casualties * t2Rates.medical_gases.oxygen_liters);

  // SI-02 FIX: Replace hardcoded 1.0 (T1) and 0.5 (T2) surgical case fractions.
  // Old values were approximations; authoritative rates are in SURGERY_REQUIREMENTS_BY_TRIAGE.
  // T1_IMMEDIATE = 95% need surgery; T2_DELAYED = 70% need surgery.
  const surgicalCases = 
    (t1Casualties * SURGERY_REQUIREMENTS_BY_TRIAGE.T1_IMMEDIATE.surgery_required_percent) +
    (t2Casualties * SURGERY_REQUIREMENTS_BY_TRIAGE.T2_DELAYED.surgery_required_percent);
  consumption.surgical_kits += surgicalCases * 0.5;
  consumption.anesthesia_doses += surgicalCases;

  // SI-03 FIX: Replace hardcoded trauma item rate literals with TRAUMA_ITEM_RATES
  // from casualtyCalculatorConstants.js (chest=0.30, hemostatic=0.25, tourniquet=0.20,
  // burn=0.10, splint=0.15). Sources: NATO AMedP-9.1, Ukraine 2022-2024, CoTCCC.
  consumption.chest_seals        += t1Casualties * TRAUMA_ITEM_RATES.chest_seal_per_t1;
  consumption.hemostatic_agents  += (t1Casualties + t2Casualties) * TRAUMA_ITEM_RATES.hemostatic_agent_per_t1_t2;
  consumption.tourniquets        += (t1Casualties + t2Casualties) * TRAUMA_ITEM_RATES.tourniquet_per_t1_t2;
  consumption.burn_dressings     += dailyCasualties * TRAUMA_ITEM_RATES.burn_dressing_per_casualty;
  consumption.splints             = (t1Casualties + t2Casualties) * TRAUMA_ITEM_RATES.splint_per_t1_t2;

  // Legacy compatibility mappings — derived from canonical fields
  consumption.medications_doses = consumption.controlled_substances_doses + consumption.antibiotics_doses;
  consumption.pain_medications  = consumption.controlled_substances_doses;
  consumption.field_dressings   = consumption.dressings_sets;
  consumption.blood_units       = consumption.prbc_units + consumption.ffp_units;

  return consumption;
}

/**
 * Calculate personnel sustainment consumption (medical staff)
 */
function calculatePersonnelConsumption(scenario) {
  const consumption = getEmptyConsumption();
  
  const totalMedicalPersonnel = calculateTotalMedicalPersonnel(scenario);
  
  // Food rations for medical personnel
  consumption.food_rations = totalMedicalPersonnel;
  
  // Medical staff also need basic medical supplies
  consumption.medications_doses += totalMedicalPersonnel * 0.1;
  consumption.dressings_sets += totalMedicalPersonnel * 0.05;
  
  return consumption;
}

/**
 * Calculate total medical personnel from all sources
 */
export function calculateTotalMedicalPersonnel(scenario) {
  let total = 0;
  
  if (!scenario?.medical_configuration) {
    return total;
  }

  // MTF personnel (estimated from beds/capabilities)
  if (scenario.medical_configuration.mtf_structure) {
    const mtf = scenario.medical_configuration.mtf_structure;
    
    // Personnel estimates per role level
    total += (mtf.role1?.count || 0) * 5;    // ~5 per BAS
    total += (mtf.role2?.count || 0) * 50;   // ~50 per Role 2
    total += (mtf.role2e?.count || 0) * 70;  // ~70 per Role 2E
    total += (mtf.role3?.count || 0) * 250;  // ~250 per Role 3
  }

  // CSU/PHA personnel (from Tab 3)
  if (scenario.medical_configuration.staging_facilities) {
    scenario.medical_configuration.staging_facilities.forEach(facility => {
      if (facility.personnel?.total) {
        total += facility.personnel.total;
      } else if (facility.personnel) {
        total += (facility.personnel.medical_officers || 0) +
                 (facility.personnel.nurses || 0) +
                 (facility.personnel.medics || 0) +
                 (facility.personnel.support_staff || 0);
      }
    });
  }

  // HLZ personnel (from Tab 3)
  if (scenario.medical_configuration.helicopter_facilities) {
    scenario.medical_configuration.helicopter_facilities.forEach(hlz => {
      if (hlz.personnel?.total) {
        total += hlz.personnel.total;
      }
    });
  }

  return total;
}

// ============================================================================
// SUPPLY TRACKING AND PROJECTION
// ============================================================================

/**
 * Calculate supply stock levels over operation duration
 * @param {object} scenario - Complete scenario object
 * @param {array} casualtiesPerDay - Daily casualties array
 * @returns {object} Stock levels and warnings
 */
export function calculateSupplyProjection(scenario, casualtiesPerDay) {
  // FIX-SI-009: Use DEFAULT_ROLE_2_STOCK as fallback when initial stock not configured
  let initialStock = scenario?.logistics?.medical_supplies?.initial_stock;
  
  if (!initialStock) {
    console.warn('No initial stock data found, using DEFAULT_ROLE_2_STOCK');
    initialStock = DEFAULT_ROLE_2_STOCK || {
      prbc_units: 100,
      ffp_units: 80,
      platelets_units: 40,
      iv_fluids_liters: 500,
      surgical_kits: 20,
      dressings_sets: 200
    };
  }

  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const resupplyEvents = scenario?.logistics?.medical_supplies?.resupply_events || [];

  // Initialize daily tracking
  const dailyStock = [];
  const dailyConsumption = [];
  const warnings = [];

  // Handle both flat and detailed_items structure
  let currentStock = initialStock.detailed_items ? 
    { ...initialStock.detailed_items } : 
    { ...initialStock };

  // Calculate for each day
  for (let day = 1; day <= duration; day++) {
    // Calculate consumption for this day
    const consumption = calculateDailySupplyConsumption(day, scenario, casualtiesPerDay);
    dailyConsumption.push({ day, ...consumption });

    // Apply consumption
    Object.keys(consumption).forEach(item => {
      currentStock[item] = Math.max(0, (currentStock[item] || 0) - consumption[item]);
    });

    // Check for resupply events on this day
    const resupply = resupplyEvents.find(event => event.day === day);
    if (resupply && resupply.supplies) {
      Object.keys(resupply.supplies).forEach(item => {
        currentStock[item] = (currentStock[item] || 0) + resupply.supplies[item];
      });
    }

    // Store current stock
    dailyStock.push({ day, ...currentStock });

    // Generate warnings using MDOS_STANDARDS from constants
    const dayWarnings = generateSupplyWarnings(day, currentStock, consumption, scenario);
    warnings.push(...dayWarnings);
  }

  return {
    dailyStock,
    dailyConsumption,
    warnings,
    finalStock: currentStock
  };
}

/**
 * Generate supply warnings based on stock levels and consumption
 * Uses thresholds from MDOS_STANDARDS in classVIII.js
 */
function generateSupplyWarnings(day, currentStock, dailyConsumption, scenario) {
  const warnings = [];
  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });

  // =========================================================================
  // FIX-SI-005: Use BLOOD_ALERT_LEVELS from bloodProducts.js instead of hardcoded values
  // =========================================================================
  
  // Get blood thresholds from constants
  const bloodAlertThresholds = BLOOD_ALERT_LEVELS || {
    CRITICAL: { days_supply: 2, stock_percent: 15 },
    LOW: { days_supply: 5, stock_percent: 30 },
    ADEQUATE: { days_supply: 10, stock_percent: 50 }
  };

  // Critical items with thresholds - blood products use constants
  const criticalItems = {
    prbc_units: { 
      threshold: bloodAlertThresholds.LOW?.stock_percent || 30, 
      critical: bloodAlertThresholds.CRITICAL?.stock_percent || 15, 
      name: 'PRBC Units',
      category: 'B',
      isBloodProduct: true
    },
    ffp_units: { 
      threshold: bloodAlertThresholds.LOW?.stock_percent || 30, 
      critical: bloodAlertThresholds.CRITICAL?.stock_percent || 15, 
      name: 'FFP Units',
      category: 'B',
      isBloodProduct: true
    },
    platelets_units: {
      threshold: bloodAlertThresholds.LOW?.stock_percent || 30,
      critical: bloodAlertThresholds.CRITICAL?.stock_percent || 15,
      name: 'Platelet Units',
      category: 'B',
      isBloodProduct: true
    },
    cryo_units: {
      threshold: bloodAlertThresholds.LOW?.stock_percent || 30,
      critical: bloodAlertThresholds.CRITICAL?.stock_percent || 15,
      name: 'Cryoprecipitate Units',
      category: 'B',
      isBloodProduct: true
    },
    blood_units: { 
      threshold: 50, 
      critical: 25, 
      name: 'Blood Units (Total)',
      category: 'B',
      isBloodProduct: true
    },
    surgical_kits: { 
      threshold: 15, 
      critical: 7, 
      name: 'Surgical Kits',
      category: 'C'
    },
    iv_fluids_liters: { 
      threshold: 200, 
      critical: 100, 
      name: 'IV Fluids',
      category: 'A'
    },
    anesthetics_uses: { 
      threshold: 30, 
      critical: 15, 
      name: 'Anesthesia',
      category: 'A'
    },
    antibiotics_doses: { 
      threshold: 100, 
      critical: 50, 
      name: 'Antibiotics',
      category: 'A'
    },
    oxygen_liters: { 
      threshold: 5000, 
      critical: 2000, 
      name: 'Medical Oxygen',
      category: 'H'
    }
  };

  Object.entries(criticalItems).forEach(([item, config]) => {
    const stock = currentStock[item] || 0;
    const consumption = dailyConsumption[item] || 0;
    
    // Calculate days of supply remaining
    const daysOfSupply = consumption > 0 ? stock / consumption : 999;

    // DEAD-006: use nation-specific DOS warning threshold where available
    const _natStd  = scenario?.force?.national_standard || 'NATO';
    const _natReqs = getStandardConfig?.(_natStd)?.medical_logistics || null;
    const minDays = MDOS_STANDARDS.MINIMUM.days;                               // 5 — critical floor, same for all
    const lowDays = _natReqs?.warning_dos_threshold ?? MDOS_STANDARDS.LOW.days; // nation-specific or 10

    // =========================================================================
    // FIX-SI-001: Use getBloodAlertLevel() for blood products
    // =========================================================================
    let severity = null;
    let alertInfo = null;
    
    if (config.isBloodProduct && getBloodAlertLevel) {
      // Use constants-based blood alert level detection
      alertInfo = getBloodAlertLevel(stock, consumption, item);
      if (alertInfo && alertInfo.level !== 'ADEQUATE' && alertInfo.level !== 'NORMAL') {
        severity = alertInfo.level === 'CRITICAL' ? 'CRITICAL' : 'WARNING';
      }
    } else {
      // Standard MDOS-based threshold check for non-blood items
      if (daysOfSupply < minDays || stock < config.critical) {
        severity = 'CRITICAL';
      } else if (daysOfSupply < lowDays || stock < config.threshold) {
        severity = 'WARNING';
      }
    }

    if (severity === 'CRITICAL') {
      // Calculate remaining operation days and urgency
      const remainingOpDays = duration - day;
      const willRunOutBeforeEnd = daysOfSupply < remainingOpDays;
      
      warnings.push({
        day,
        severity: 'CRITICAL',
        item,
        itemName: config.name,
        category: config.category,
        currentStock: Math.round(stock),
        daysOfSupply: Math.round(daysOfSupply * 10) / 10,
        operationDaysRemaining: remainingOpDays,
        willRunOutBeforeEnd: willRunOutBeforeEnd,
        message: `${config.name} CRITICAL: Only ${Math.round(stock)} units remaining (${Math.round(daysOfSupply * 10) / 10} days supply)`,
        alertLevel: alertInfo?.level || 'CRITICAL',
        recommendations: [
          'Schedule emergency resupply IMMEDIATELY',
          'Reduce non-essential consumption',
          config.isBloodProduct ? 'Consider activating Walking Blood Bank' : 'Prioritize essential cases only',
          config.isBloodProduct ? 'Activate MTP protocol for critical patients' : null,
          willRunOutBeforeEnd ? `WARNING: Stock will deplete before operation ends (${remainingOpDays} days remaining)` : null
        ].filter(Boolean)
      });
    } else if (severity === 'WARNING') {
      // Calculate remaining operation days
      const remainingOpDays = duration - day;
      
      warnings.push({
        day,
        severity: 'WARNING',
        item,
        itemName: config.name,
        category: config.category,
        currentStock: Math.round(stock),
        daysOfSupply: Math.round(daysOfSupply * 10) / 10,
        operationDaysRemaining: remainingOpDays,
        message: `${config.name} low: ${Math.round(stock)} units remaining (${Math.round(daysOfSupply * 10) / 10} days supply)`,
        alertLevel: alertInfo?.level || 'LOW',
        recommendations: [
          `Schedule resupply for Day ${day + 2}`,
          'Monitor consumption closely',
          'Prepare alternative sourcing'
        ]
      });
    }
  });

  return warnings;
}

// ============================================================================
// MDOS CALCULATION
// Wrapper around constants function with scenario integration
// ============================================================================

/**
 * Calculate MDOS for current scenario
 * Uses calculateMDOS from classVIII.js constants
 */
/**
 * Calculate MDOS for complete scenario
 * Uses classVIII.calculateMDOS with proper data extraction
 * 
 * VERSION 2.3.1 FIX: Uses DEFAULT_CASUALTY_MIX from classVIII.js - NO hardcoded percentages
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {Object} MDOS analysis results
 */
export function calculateScenarioMDOS(scenario) {
  if (!scenario?.logistics?.medical_supplies?.initial_stock) {
    return null;
  }

  const stock = scenario.logistics.medical_supplies.initial_stock;
  const avgDailyCasualties = getAverageDailyCasualties(scenario);
  
  // Use DEFAULT_CASUALTY_MIX from classVIII.js - NO hardcoded percentages
  let casualtyMix = scenario.medical_configuration?.casualty_mix;
  
  // If no explicit mix configured, calculate from triage breakdown
  if (!casualtyMix && scenario.casualty_projections?.by_triage) {
    const bt = scenario.casualty_projections.by_triage;
    // Support both uppercase (T1) and lowercase (t1) keys
    const t1 = bt.T1 || bt.t1 || 0;
    const t2 = bt.T2 || bt.t2 || 0;
    const t3 = bt.T3 || bt.t3 || 0;
    const combatTotal = t1 + t2 + t3;
    
    if (combatTotal > 0) {
      // Calculate actual distribution within combat casualties
      // Scale to match total combat percentage from DEFAULT_CASUALTY_MIX
      const combatPct = DEFAULT_CASUALTY_MIX.t1 + DEFAULT_CASUALTY_MIX.t2 + DEFAULT_CASUALTY_MIX.t3;
      casualtyMix = {
        t1: (t1 / combatTotal) * combatPct,
        t2: (t2 / combatTotal) * combatPct,
        t3: (t3 / combatTotal) * combatPct,
        dnbi: DEFAULT_CASUALTY_MIX.dnbi
      };
    }
  }

  return constantsCalculateMDOS(stock, avgDailyCasualties, casualtyMix);
}

/**
 * Get average daily casualties from scenario
 * 
 * VERSION 2.3 FIX: Properly reads from all possible casualty_projections paths
 * 
 * Data path priority:
 * 1. casualty_projections.daily_average (if explicitly provided)
 * 2. casualty_projections.total / duration (most common - from preliminary calculations)
 * 3. casualty_projections.by_corps.total_casualties / duration (legacy)
 * 4. casualty_projections.by_brigade[].total_casualties / duration (legacy)
 * 5. force.total_strength * 0.01 (fallback estimate)
 * 
 * @param {Object} scenario - Complete scenario object
 * @returns {number} Average daily casualties
 */
function getAverageDailyCasualties(scenario) {
  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  
  // Priority 1: Use daily_average if explicitly provided
  if (scenario.casualty_projections?.daily_average > 0) {
    return scenario.casualty_projections.daily_average;
  }
  
  // Priority 2: Calculate from total (most common case from Tab4/preliminary calculations)
  if (scenario.casualty_projections?.total > 0) {
    return scenario.casualty_projections.total / duration;
  }
  
  // Priority 3: Legacy - by_corps structure
  if (scenario.casualty_projections?.by_corps?.total_casualties > 0) {
    return scenario.casualty_projections.by_corps.total_casualties / duration;
  }
  
  // Priority 4: Legacy - by_brigade structure  
  if (scenario.casualty_projections?.by_brigade?.length > 0) {
    const total = scenario.casualty_projections.by_brigade.reduce(
      (sum, b) => sum + (b.total_casualties || 0), 0
    );
    if (total > 0) {
      return total / duration;
    }
  }
  
  // Priority 5: Estimate from force strength (fallback)
  // v2.4 FIX: Check both property names (Tab1 saves as totalStrength camelCase)
  const forceStrength = resolveInput(scenario.force?.totalStrength || 
                        scenario.force?.total_strength || 
                        scenario.force?.forceBuilderResult?.corps?.strength, 'force_total_strength', { zeroIsMissing: true });
  // SI-07 FIX: Replace hardcoded 0.01 with BASE_CASUALTY_RATE.daily.
  // BASE_CASUALTY_RATE = { daily: 0.015 } (object) from casualtyCalculatorConstants.js.
  // 0.01 = 10/1000/day was an underestimate; correct baseline is 15/1000 = 0.015.
  // NOTE: BASE_CASUALTY_RATE is an OBJECT — use .daily property, not division.
  // Previous session used `BASE_CASUALTY_RATE / 1000` → NaN (object / number). Fixed here.
  const casualtyRate = BASE_CASUALTY_RATE.daily;
  return forceStrength * casualtyRate;
}

// ============================================================================
// CONSUMPTION PATTERN ANALYSIS
// ============================================================================

/**
 * Analyze consumption patterns and detect anomalies
 * @param {array} dailyConsumption - Array of daily consumption objects
 * @returns {object} Pattern analysis and anomalies
 */
export function analyzeConsumptionPatterns(dailyConsumption) {
  if (!dailyConsumption || dailyConsumption.length === 0) {
    return { patterns: [], anomalies: [], warnings: [] };
  }

  const patterns = [];
  const anomalies = [];
  const warnings = [];

  // Calculate baseline consumption (average of first 3 days)
  const baselineDays = Math.min(3, dailyConsumption.length);
  const baseline = {};
  
  const items = Object.keys(dailyConsumption[0]).filter(key => key !== 'day');
  
  items.forEach(item => {
    let sum = 0;
    for (let i = 0; i < baselineDays; i++) {
      sum += dailyConsumption[i][item] || 0;
    }
    baseline[item] = sum / baselineDays;
  });

  // Analyze each day for anomalies
  dailyConsumption.forEach((dayData, index) => {
    if (index < baselineDays) return;

    items.forEach(item => {
      const current = dayData[item] || 0;
      const expected = baseline[item] || 0;
      
      if (expected > 0) {
        const variance = (current - expected) / expected;
        
        // Use SURGE_FACTORS from constants for thresholds
        if (variance > RESUPPLY_FACTORS.SURGE_FACTORS.MASCAL_CRITICAL - 1) { // 300%
          anomalies.push({
            day: dayData.day,
            item,
            severity: 'HIGH',
            actual: Math.round(current),
            expected: Math.round(expected),
            variance: Math.round(variance * 100),
            message: `${item}: Consumption ${Math.round(variance * 100)}% above baseline`,
            possibleCauses: ['MASCAL event', 'Major casualties', 'Facility malfunction', 'Data error']
          });
        } else if (variance > RESUPPLY_FACTORS.SURGE_FACTORS.MASCAL_BRAVO - 1) { // 200%
          anomalies.push({
            day: dayData.day,
            item,
            severity: 'MEDIUM',
            actual: Math.round(current),
            expected: Math.round(expected),
            variance: Math.round(variance * 100),
            message: `${item}: Consumption ${Math.round(variance * 100)}% above baseline`,
            possibleCauses: ['Increased tempo', 'Higher casualty rate', 'Additional facility']
          });
        }
      }
    });
  });

  // Calculate patterns
  items.forEach(item => {
    const values = dailyConsumption.map(day => day[item] || 0);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    
    patterns.push({
      item,
      average: Math.round(avg * 10) / 10,
      max: Math.round(max),
      min: Math.round(min),
      variability: max > 0 ? Math.round((max - min) / max * 100) : 0
    });
  });

  return { patterns, anomalies, warnings };
}

// ============================================================================
// SMART RECOMMENDATIONS
// ============================================================================

/**
 * Generate smart recommendations based on supply status
 * Uses PUSH_PACKAGES from classVIII.js for suggestions
 */
export function generateSmartRecommendations(supplyProjection, scenario) {
  if (!supplyProjection || !scenario) {
    return [];
  }

  const recommendations = [];
  const warnings = supplyProjection.warnings;
  const duration = resolveInput(scenario.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const nationalStandard = scenario.force?.national_standard || 'NATO';
  
  // FIX-SI-010: Get national standard requirements for supply planning
  const nationalReqs = NATIONAL_STANDARDS?.[nationalStandard]?.medical_logistics || null;
  const requiredMdos = nationalReqs?.minimum_mdos_days || 7;

  // Group warnings by item
  const warningsByItem = {};
  warnings.forEach(warning => {
    if (!warningsByItem[warning.item]) {
      warningsByItem[warning.item] = [];
    }
    warningsByItem[warning.item].push(warning);
  });

  // Generate recommendations for critical items
  Object.entries(warningsByItem).forEach(([item, itemWarnings]) => {
    const criticalCount = itemWarnings.filter(w => w.severity === 'CRITICAL').length;
    
    if (criticalCount > 0) {
      const firstCritical = itemWarnings.find(w => w.severity === 'CRITICAL');
      
      // =========================================================================
      // FIX-SI-006: Use getResupplyRequirements from classVIII.js
      // =========================================================================
      let resupplyInfo = null;
      if (getResupplyRequirements) {
        resupplyInfo = getResupplyRequirements(item, firstCritical.currentStock, firstCritical.daysOfSupply);
      }
      
      // =========================================================================
      // FIX-SI-007: Use CLASS_VIII_CATEGORIES for item classification
      // =========================================================================
      let itemCategory = 'OTHER';
      if (CLASS_VIII_CATEGORIES) {
        // Find category for this item
        Object.entries(CLASS_VIII_CATEGORIES).forEach(([categoryKey, categoryInfo]) => {
          if (categoryInfo.items && categoryInfo.items.includes(item)) {
            itemCategory = categoryKey;
          }
        });
      }
      
      // Suggest appropriate push package from constants
      let suggestedPackage = 'TRAUMA_RESUPPLY';
      if (item.includes('blood') || item.includes('prbc') || item.includes('ffp')) {
        suggestedPackage = 'BLOOD_RESUPPLY';
      } else if (item.includes('surgical')) {
        suggestedPackage = 'SURGICAL_RESUPPLY';
      } else if (item.includes('anesthesia') || item.includes('antibiotic') || item.includes('controlled')) {
        suggestedPackage = 'PHARMACEUTICAL_RESUPPLY';
      }

      const packageInfo = PUSH_PACKAGES[suggestedPackage];

      recommendations.push({
        priority: 'HIGH',
        category: 'RESUPPLY',
        item: firstCritical.itemName,
        itemCategory: itemCategory, // FIX-SI-007: Include item classification
        message: `Emergency resupply needed for ${firstCritical.itemName}`,
        action: `Schedule emergency convoy for Day ${firstCritical.day}`,
        impact: 'Critical medical capability at risk',
        timeframe: 'IMMEDIATE',
        suggestedPackage: packageInfo?.label,
        packageContents: packageInfo?.contents,
        resupplyQuantity: resupplyInfo?.quantity || null, // FIX-SI-006: Include calculated quantity
        resupplyPriority: resupplyInfo?.priority || 'IMMEDIATE' // FIX-SI-006: Include resupply priority
      });
    }
  });

  // Check Walking Blood Bank activation
  // =========================================================================
  // FIX-SI-004: Use BLOOD_PRODUCTS specs and WALKING_BLOOD_BANK constants
  // =========================================================================
  const bloodWarnings = warningsByItem['prbc_units'] || warningsByItem['blood_units'] || [];
  const criticalBlood = bloodWarnings.filter(w => w.severity === 'CRITICAL');
  
  if (criticalBlood.length > 0) {
    const wbb = scenario.logistics?.walking_blood_bank;
    
    // Use WALKING_BLOOD_BANK constants if available
    const wbbConstants = WALKING_BLOOD_BANK || {
      donation_volume_ml: 450,
      collection_time_minutes: 15,
      screening_time_minutes: 5,
      max_donations_per_donor_per_month: 1
    };
    
    if (wbb?.enabled && wbb?.trained_donors > 0) {
      // Calculate potential yield using constants
      const potentialUnits = Math.floor(wbb.trained_donors * 0.5);
      const collectionTime = wbbConstants.collection_time_minutes + wbbConstants.screening_time_minutes;
      
      recommendations.push({
        priority: 'HIGH',
        category: 'WALKING_BLOOD_BANK',
        item: 'Blood Products',
        message: 'Walking Blood Bank activation recommended',
        action: `Activate WBB on Day ${criticalBlood[0].day}`,
        impact: `Can provide up to ${potentialUnits} units fresh whole blood`,
        timeframe: 'IMMEDIATE',
        // FIX-SI-004: Add detailed WBB info from constants
        wbb_details: {
          trained_donors: wbb.trained_donors,
          potential_units: potentialUnits,
          collection_time_per_donor_min: collectionTime,
          donation_volume_ml: wbbConstants.donation_volume_ml,
          source: 'WALKING_BLOOD_BANK constants'
        }
      });
    } else {
      // WBB not available - suggest establishing one
      recommendations.push({
        priority: 'MEDIUM',
        category: 'WALKING_BLOOD_BANK',
        item: 'Blood Products',
        message: 'Consider establishing Walking Blood Bank capability',
        action: 'Screen and train potential donors from unit personnel',
        impact: 'Emergency blood supply when resupply delayed',
        timeframe: 'PREPARATORY'
      });
    }
    
    // FIX-SI-004: Add blood product storage recommendations using BLOOD_PRODUCTS
    if (BLOOD_PRODUCTS) {
      const prbcSpecs = BLOOD_PRODUCTS.PRBC || BLOOD_PRODUCTS.prbc;
      const ffpSpecs = BLOOD_PRODUCTS.FFP || BLOOD_PRODUCTS.ffp;
      
      if (prbcSpecs || ffpSpecs) {
        recommendations.push({
          priority: 'MEDIUM',
          category: 'BLOOD_STORAGE',
          item: 'Blood Products',
          message: 'Verify cold chain integrity for blood products',
          action: 'Check storage temperatures and expiration dates',
          impact: 'Prevent wastage of critical blood products',
          timeframe: 'ONGOING',
          storage_specs: {
            prbc: prbcSpecs ? {
              storage_temp: prbcSpecs.storage_temp || '1-6°C',
              shelf_life: prbcSpecs.shelf_life || '42 days'
            } : null,
            ffp: ffpSpecs ? {
              storage_temp: ffpSpecs.storage_temp || '-18°C or below',
              shelf_life: ffpSpecs.shelf_life || '1 year frozen'
            } : null
          }
        });
      }
    }
  }

  // MASCAL surge recommendation
  const mascalDays = warnings.filter(w => 
    w.severity === 'CRITICAL' && 
    w.daysOfSupply < 2
  ).length;

  if (mascalDays > 0) {
    recommendations.push({
      priority: 'HIGH',
      category: 'MASCAL_PLANNING',
      message: 'MASCAL surge capability needed',
      action: 'Pre-position MASCAL Surge Package',
      suggestedPackage: PUSH_PACKAGES.MASCAL_SURGE.label,
      packageContents: PUSH_PACKAGES.MASCAL_SURGE.contents
    });
  }

  // FIX-SI-010: Add national standard compliance recommendation
  if (nationalReqs && duration > requiredMdos) {
    // Check if resupply is planned for long operations
    const plannedResupplies = scenario?.logistics?.medical_supplies?.resupply_events?.length || 0;
    const requiredResupplies = Math.ceil((duration - requiredMdos) / requiredMdos);
    
    if (plannedResupplies < requiredResupplies) {
      recommendations.push({
        priority: 'MEDIUM',
        category: 'NATIONAL_STANDARD_COMPLIANCE',
        message: `${nationalStandard} standard requires ${requiredMdos}-day MDOS`,
        action: `Plan ${requiredResupplies - plannedResupplies} additional resupply events for ${duration}-day operation`,
        impact: `Current ${plannedResupplies} resupply events may be insufficient`,
        national_standard: nationalStandard,
        operation_duration: duration,
        required_mdos: requiredMdos
      });
    }
  }

  // Sort by priority
  const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
  recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return recommendations;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate supply configuration
 * Uses validateStockLevels from classVIII.js
 */
export function validateSupplyConfiguration(scenario) {
  const errors = [];
  const warnings = [];

  if (!scenario?.logistics?.medical_supplies?.initial_stock) {
    errors.push({
      field: 'initial_stock',
      message: 'Initial supply stock not configured',
      severity: 'ERROR'
    });
    return { valid: false, errors, warnings };
  }

  const stock = scenario.logistics.medical_supplies.initial_stock;
  
  // Use validation from constants
  const constantsValidation = validateStockLevels(stock);
  errors.push(...constantsValidation.errors.map(e => ({ ...e, severity: 'ERROR' })));
  warnings.push(...constantsValidation.warnings.map(w => ({ ...w, severity: 'WARNING' })));

  // Check days of supply
  if (stock.days_of_supply !== undefined) {
    if (stock.days_of_supply < MDOS_STANDARDS.MINIMUM.days) {
      errors.push({
        field: 'days_of_supply',
        message: `Insufficient supply stock (minimum ${MDOS_STANDARDS.MINIMUM.days} days required)`,
        severity: 'ERROR'
      });
    } else if (stock.days_of_supply < MDOS_STANDARDS.LOW.days) {
      warnings.push({
        field: 'days_of_supply',
        message: `Supply stock below NATO recommendation (${MDOS_STANDARDS.ADEQUATE.days} days)`,
        severity: 'WARNING'
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named export object for ESLint compliance (import/no-anonymous-default-export)
const supplyIntelligenceModule = {
  calculateDailySupplyConsumption,
  calculateSupplyProjection,
  calculateScenarioMDOS,
  analyzeConsumptionPatterns,
  generateSmartRecommendations,
  validateSupplyConfiguration,
  calculateTotalMedicalPersonnel
};

export default supplyIntelligenceModule;
